import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import {
  Send as SendIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as SmartToyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from '../../config/axios';
import { io, Socket } from 'socket.io-client';
import { getApiUrl, getWebSocketUrl } from '../../config/environment';
import BotToggleButton from '../../components/BotToggleButton';
import QuickMessagesButton from '../../components/QuickMessagesButton';
import AttendantAssignment from '../../components/AttendantAssignment';
import ChatSupportEventsBanner from '../../components/ChatSupportEventsBanner';

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  custom_fields?: {
    whatsapp_id?: string;
    username?: string;
  };
}

interface Conversation {
  id: string;
  contact_id: string;
  channel_id: string;
  external_id?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  contact?: Contact;
  channel?: {
    id: string;
    name: string;
    type: string;
    status: string;
  };
}

interface Message {
  id: string;
  conversation_id: string;
  external_id?: string;
  content: string;
  direction: 'inbound' | 'outbound';
  sent_at: string;
  status: string;
  content_type?: string;
  attachments?: Array<{
    type: string;
    url: string;
    file_id?: string;
    width?: number;
    height?: number;
    file_name?: string;
    mime_type?: string;
    duration?: number;
    title?: string;
    performer?: string;
    emoji?: string;
  }>;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  status: string;
}



const WhatsAppRecovery: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  
  // Constantes para URLs da API e WebSocket
  const API_BASE_URL = getApiUrl();
  const WS_URL = getWebSocketUrl();
  
  // 🔍 NOVO: Estado para armazenar o channel_id do recovery padrão
  const [recoveryChannelId, setRecoveryChannelId] = useState<string | null>(null);
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const selectedConversationRef = useRef<string>('');
  
  // Atualizar ref quando selectedConversation mudar
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState<string | null>(null);
  const [deletingContact, setDeletingContact] = useState<string | null>(null);
  const [openLeadDetailsDialog, setOpenLeadDetailsDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [stage4Leads, setStage4Leads] = useState<Set<string>>(new Set());
  const [allLeadsStages, setAllLeadsStages] = useState<Map<string, number>>(new Map());
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const recordingIntervalRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  /** Ao cancelar durante gravação, não criar ficheiro no onstop */
  const discardRecordingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollContainerRef = useRef<HTMLDivElement>(null); // Ref para o container de scroll
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 3; // Limitar tentativas de reconexão

  // 🔍 NOVO: Buscar o channel_id do recovery padrão na inicialização
  useEffect(() => {
    const fetchRecoveryChannel = async () => {
      try {
        console.log('🔍 Buscando canal de recovery padrão...');
        const response = await axios.get(`${API_BASE_URL}/channels`);
        if (response.data.success) {
          const channels = response.data.data || [];
          // Buscar canal de recovery padrão (sem config de inbox dinâmico)
          const recoveryChannel = channels.find((ch: any) => 
            ch.type === 'whatsapp_recovery' && 
            (!ch.config?.inbox_number || ch.config?.is_default === true)
          );
          
          if (recoveryChannel) {
            console.log('✅ Canal de recovery padrão encontrado:', recoveryChannel.id);
            setRecoveryChannelId(recoveryChannel.id);
          } else {
            console.warn('⚠️ Canal de recovery padrão não encontrado');
          }
        }
      } catch (error) {
        console.error('❌ Erro ao buscar canal de recovery:', error);
      }
    };
    
    fetchRecoveryChannel();
  }, [API_BASE_URL]);

  // Abrir conversa específica se contactId estiver na URL
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (contactId && recoveryChannelId) {
      // Se há um contactId na URL, buscar TODAS as conversas para garantir que leads antigos sejam encontrados
      const fetchAllConversationsForContact = async () => {
        try {
          // 🔍 MODIFICADO: Usar channel_id específico em vez de tipo
          const response = await axios.get(`${API_BASE_URL}/conversations?channel_id=${recoveryChannelId}&limit=200`);
          
          if (response.data.success) {
            const whatsappRecoveryConversations = response.data.data || [];
            
            // Encontrar a conversa que corresponde ao contato
            const conversation = whatsappRecoveryConversations.find((conv: Conversation) => conv.contact_id === contactId);
            if (conversation) {
              setSelectedConversation(conversation.id);
              // Atualizar a lista de conversas com as conversas do WhatsApp Recuperação
              setConversations(whatsappRecoveryConversations);
            }
            
            // Limpar o parâmetro da URL após abrir a conversa
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('contactId');
            window.history.replaceState({}, '', newUrl.toString());
          }
        } catch (error) {
          console.error('Error fetching WhatsApp Recuperação conversations for contact:', error);
        }
      };
      
      fetchAllConversationsForContact();
    }
  }, [searchParams, recoveryChannelId, API_BASE_URL]);

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (name: string) => {
    const colors = [
      '#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5',
      '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
      '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
      '#ff5722', '#795548', '#607d8b'
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Função para atualizar avatar do contato
  const updateContactAvatar = async (contactId: string) => {
    try {
      setUpdatingAvatar(contactId);
      const response = await axios.post(`${API_BASE_URL}/contacts/${contactId}/update-avatar`);
      
      if (response.data.success) {
        // Atualizar a lista de conversas para refletir a mudança
        await fetchConversations();
        
        // Se a conversa estiver selecionada, atualizar as mensagens também
        if (selectedConversation) {
          await fetchMessages(selectedConversation);
        }
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      setError('Erro ao atualizar foto de perfil');
    } finally {
      setUpdatingAvatar(null);
    }
  };

  // Função para excluir contato
  const deleteContact = async (contactId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setDeletingContact(contactId);
      const response = await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
      
      if (response.data.success) {
        // Remover a conversa da lista
        setConversations(prev => prev.filter(conv => conv.contact_id !== contactId));
        
        // Se a conversa excluída estava selecionada, limpar a seleção
        if (selectedConversation && conversations.find(c => c.id === selectedConversation)?.contact_id === contactId) {
          setSelectedConversation('');
          setMessages([]);
        }
        
        setError('');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Erro ao excluir contato');
    } finally {
      setDeletingContact(null);
    }
  };

  // Função para scroll automático para a última mensagem
  const scrollToBottom = () => {
    // Scroll apenas no container de mensagens, não na página toda
    if (messagesScrollContainerRef.current) {
      messagesScrollContainerRef.current.scrollTo({
        top: messagesScrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    } else if (messagesEndRef.current) {
      // Fallback: scrollIntoView apenas se não encontrar container
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // Função para normalizar URLs de anexos
  const normalizeAttachmentUrl = (url: string | undefined): string => {
    if (!url) return '';
    // Se já é uma URL completa (http/https), retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Se for URL relativa, construir URL completa usando a base da API
    const apiBase = API_BASE_URL.replace('/api/v1', '');
    return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/${url}`;
  };

  // Função para buscar conversas (memoizada)
  const fetchConversations = useCallback(async (reset: boolean = true) => {
    try {
      setError('');
      
      if (reset) {
        setConversationsPage(1);
        setHasMoreConversations(true);
      }
      
      const page = reset ? 1 : conversationsPage;
      const limit = 100; // Reduzir de 200 para 100 para carregar mais rápido
      
      // Buscar conversas do banco de dados com paginação (apenas uma vez)
      const statusFilter = showArchived ? 'archived' : 'open';
      const url = `${API_BASE_URL}/conversations?channel=whatsapp_recovery&limit=${limit}&page=${page}&sortBy=last_message_at&sortOrder=desc&status=${statusFilter}`;
      console.log('🌐 [WhatsApp Recovery] Buscando conversas:', url);
      
      const conversationsResponse = await axios.get(url, {
        timeout: 30000
      });
      
      if (conversationsResponse.data.success) {
        const newConversations: Conversation[] = conversationsResponse.data.data || [];
        const pagination = conversationsResponse.data.pagination;
        
        // Log detalhado das conversas recebidas
        console.log('📦 [WhatsApp Recovery] Conversas recebidas:', newConversations.length);
        if (newConversations.length > 0) {
          console.log('📋 [WhatsApp Recovery] Primeiras 3 conversas:', newConversations.slice(0, 3).map(c => ({
            id: c.id,
            canal_tipo: c.channel?.type,
            canal_nome: c.channel?.name,
            contato: c.contact?.name
          })));
        }
        
        // Filtrar conversas vazias (sem last_message_at válido)
        const conversationsWithMessages = newConversations.filter((conv: any) => {
          return conv.last_message_at && conv.last_message_at !== null;
        });
        
        console.log(`🔍 [WhatsApp Recovery] Conversas antes do filtro: ${newConversations.length}, depois: ${conversationsWithMessages.length}`);
        
        // Verificar se há mais conversas para carregar
        const hasMore = pagination ? page < pagination.totalPages : conversationsWithMessages.length === limit;
        setHasMoreConversations(hasMore);
        
        // Ordenar conversas por last_message_at (mais recentes primeiro)
        const sortedNewConversations = conversationsWithMessages.sort((a: any, b: any) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        if (reset) {
          setConversations(sortedNewConversations);
          // Verificar estágios de todos os leads
          await checkAllLeadsStages(sortedNewConversations);
        } else {
          // Adicionar novas conversas às existentes, evitando duplicatas
          setConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = sortedNewConversations.filter(c => !existingIds.has(c.id));
            const combined = [...prev, ...uniqueNew].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            return combined;
          });
        }
        
        // Atualizar página
        setConversationsPage(page + 1);
      }
    } catch (err) {
      console.error('Erro ao buscar conversas:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar conversas');
      }
    } finally {
      setLoading(false);
      setLoadingMoreConversations(false);
    }
  }, [conversationsPage, showArchived]);

  // Função para carregar mais conversas
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConversations || !hasMoreConversations) return;
    
    setLoadingMoreConversations(true);
    await fetchConversations(false);
  }, [loadingMoreConversations, hasMoreConversations, fetchConversations]);

  // Função para arquivar conversa
  const handleArchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'archived'
      });
      
      // Atualizar estado local
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // Se a conversa arquivada estava selecionada, limpar seleção
      if (selectedConversation === conversationId) {
        setSelectedConversation('');
        setMessages([]);
      }
    } catch (err) {
      console.error('Erro ao arquivar conversa:', err);
      setError('Erro ao arquivar conversa');
    }
  }, [selectedConversation]);

  // Função para desarquivar conversa
  const handleUnarchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'open'
      });
      
      // Buscar conversa atualizada do backend
      const response = await axios.get(`${API_BASE_URL}/conversations/${conversationId}`);
      if (response.data.success) {
        const updatedConversation = response.data.data;
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversationId);
          if (exists) {
            return prev.map(c => c.id === conversationId ? updatedConversation : c);
          } else {
            return [...prev, updatedConversation].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          }
        });
      }
    } catch (err) {
      console.error('Erro ao desarquivar conversa:', err);
      setError('Erro ao desarquivar conversa');
    }
  }, []);

  // Função para marcar conversa como lida
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Marcando conversa de instância como lida:', conversationId);
        // Para conversas de instância, apenas atualizar o estado local
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unread_count: 0 }
              : conv
          );
          console.log('📱 Estado atualizado:', updated.find(c => c.id === conversationId));
          return updated;
        });
        return;
      }

      await axios.post(`${API_BASE_URL}/conversations/${conversationId}/mark-as-read`);
      
      // Atualizar a lista de conversas para refletir a mudança
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
    } catch (err) {
      console.error('Erro ao marcar conversa como lida:', err);
      // Não mostrar erro para o usuário, pois não é crítico
    }
  }, []);

  // Log das mensagens quando mudarem
  useEffect(() => {
    console.log(`🎨 Mensagens no estado: ${messages.length}`);
    messages.forEach((message, index) => {
      console.log(`📝 Mensagem ${index + 1}/${messages.length}: ${message.content?.substring(0, 30) || '(sem conteúdo)'}...`);
    });
  }, [messages]);

  // Função para buscar mensagens de uma conversa
  const fetchMessages = useCallback(async (conversationId: string) => {
    try {
      console.log(`🔍 Buscando mensagens para conversa: ${conversationId}`);
      
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Conversa de instância detectada, buscando mensagens das instâncias...');
        
        // Extrair contactId e instanceName do ID da conversa
        // O formato é: instance-{contactId}-{instanceName}
        const instanceKey = conversationId.replace('instance-', '');
        
        // O contactId é um UUID completo (36 caracteres: 8-4-4-4-12)
        // Então precisamos pegar os primeiros 36 caracteres como contactId
        const contactId = instanceKey.substring(0, 36);
        const instanceName = instanceKey.substring(37); // Pular o hífen após o UUID
        
        console.log('🔍 Extraído:', { contactId, instanceName, originalId: conversationId });
        
        // Buscar mensagens das instâncias WhatsApp
        const response = await axios.get(`${API_BASE_URL}/whatsapp-instance-messages?contact_id=${contactId}&instance_name=${instanceName}&limit=1000`, {
          timeout: 30000
        });
        
        if (response.data.success) {
          const messagesData: Message[] = response.data.data || [];
          console.log(`📋 Total de mensagens de instância recebidas: ${messagesData.length}`);
          
          // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
          const sortedMessages = messagesData.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
          console.log(`📋 Mensagens de instância ordenadas: ${sortedMessages.length}`);
          
          setMessages(sortedMessages);
          console.log(`🎯 Mensagens de instância definidas no estado: ${sortedMessages.length}`);
          
          // Scroll para a última mensagem após um pequeno delay
          setTimeout(() => {
            console.log('🔄 Executando scroll para baixo...');
            scrollToBottom();
          }, 100);
        } else {
          console.warn('Nenhuma mensagem encontrada para a conversa de instância');
          setMessages([]);
        }
        return;
      }
      
      // Para conversas normais, usar o endpoint padrão
      // Buscar mensagens ordenadas por mais recentes primeiro (desc), depois ordenar no frontend para asc
      // Limitar a 500 mensagens para melhor performance (as mais recentes)
      const response = await axios.get(`${API_BASE_URL}/messages/conversation/${conversationId}?limit=500&sortOrder=desc`, {
        timeout: 30000 // 30 segundos de timeout
      });
      
      console.log('📊 Resposta da API:', response.data);
      let messagesData: Message[] = response.data.data || [];
      console.log(`📋 Total de mensagens recebidas: ${messagesData.length}`);
      
      // Normalizar attachments para garantir que seja sempre um array
      messagesData = messagesData.map(msg => {
        if (msg.attachments) {
          if (Array.isArray(msg.attachments)) {
            return { ...msg, attachments: msg.attachments };
          } else if (typeof msg.attachments === 'string') {
            try {
              return { ...msg, attachments: JSON.parse(msg.attachments) };
            } catch (e) {
              console.error(`Erro ao parsear attachments da mensagem ${msg.id}:`, e);
              return { ...msg, attachments: [] };
            }
          } else {
            return { ...msg, attachments: [] };
          }
        } else {
          return { ...msg, attachments: [] };
        }
      });
      
      // Ordenar mensagens por data (mais antigas primeiro, mais novas no final) para exibição
      const sortedMessages = messagesData.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
      console.log(`📋 Mensagens ordenadas: ${sortedMessages.length}`);
      
      setMessages(sortedMessages);
      console.log(`🎯 Mensagens definidas no estado: ${sortedMessages.length}`);
      
      // Scroll para a última mensagem após um pequeno delay
      setTimeout(() => {
        console.log('🔄 Executando scroll para baixo...');
        scrollToBottom();
      }, 100);
    } catch (err) {
      console.error('Erro ao buscar mensagens:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar mensagens');
      }
    }
  }, []);

  // Função para enviar mensagem
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedConversation || sendingMessage) return;

    // Se houver arquivos selecionados, enviar como mídia
    if (selectedFiles.length > 0) {
      await handleSendMultipleMedia(selectedFiles);
      return;
    }

    // Caso contrário, enviar mensagem de texto normal
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content: newMessage,
        content_type: 'text'
      });

      setNewMessage('');

      // Adicionar mensagem imediatamente ao estado (otimistic update)
      if (response.data.success && response.data.data) {
        const sentMessage = response.data.data;
        // Garantir que attachments seja sempre um array
        const messageWithAttachments = {
          ...sentMessage,
          attachments: Array.isArray(sentMessage.attachments) 
            ? sentMessage.attachments 
            : (typeof sentMessage.attachments === 'string' 
                ? (() => {
                    try {
                      return JSON.parse(sentMessage.attachments);
                    } catch {
                      return [];
                    }
                  })()
                : [])
        };

        setMessages(prev => {
          // Verificar se a mensagem já existe (evitar duplicatas do WebSocket)
          const exists = prev.some(msg => msg.id === messageWithAttachments.id);
          if (exists) {
            return prev;
          }
          const newMessages = [...prev, messageWithAttachments];
          // Ordenar mensagens por data de envio (mais antigas primeiro)
          return newMessages.sort((a, b) => 
            new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
          );
        });

        // Atualizar conversa na lista
        setConversations(prev => 
          prev.map(conv => 
            conv.id === selectedConversation
              ? {
                  ...conv,
                  last_message_at: messageWithAttachments.sent_at || messageWithAttachments.created_at,
                  last_message: newMessage
                }
              : conv
          ).sort((a, b) => {
            const dateA = new Date(a.last_message_at || a.updated_at || 0);
            const dateB = new Date(b.last_message_at || b.updated_at || 0);
            return dateB.getTime() - dateA.getTime();
          })
        );

        // Scroll para o final após adicionar mensagem
        setTimeout(scrollToBottom, 100);
      }

      // Manter foco no campo de mensagem após envio
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar mídia
  const handleSendMedia = async (file: File) => {
    if (!selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedConversation);
      if (newMessage.trim()) {
        formData.append('content', newMessage);
      }
      // Se for um arquivo de áudio gravado
      if (file.name.includes('recording') || file.name.includes('voice')) {
        formData.append('is_voice', 'true');
      }

      const response = await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Sempre recarregar mensagens após envio bem-sucedido para garantir que apareçam
      setNewMessage('');
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);

      // Manter foco no campo de mensagem após envio
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar múltiplos arquivos
  const handleSendMultipleMedia = async (files: File[]) => {
    if (!selectedConversation || sendingMessage || files.length === 0) return;

    setSendingMessage(true);
    // Limpar prévia imediatamente ao clicar em enviar
    const messageToSend = newMessage;
    setNewMessage('');
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      // Enviar cada arquivo sequencialmente
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversation_id', selectedConversation);
        // Adicionar mensagem apenas no primeiro arquivo
        if (i === 0 && messageToSend.trim()) {
          formData.append('content', messageToSend);
        }
        // Se for um arquivo de áudio gravado
        if (file.name.includes('recording') || file.name.includes('voice')) {
          formData.append('is_voice', 'true');
        }

        await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Pequeno delay entre envios para não sobrecarregar
        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);
    } catch (err: any) {
      console.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para selecionar arquivos (múltiplos)
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/mpeg', 'video/quicktime',
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/m4a',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain', 'application/zip'
    ];
    const isValidFileType = (file: File) => {
      const ext = (file.name.toLowerCase().split('.').pop() || '');
      const isPdf = ext === 'pdf' && (file.type === 'application/pdf' || !file.type);
      const isDoc = (ext === 'doc' || ext === 'docx') && (['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ''].includes(file.type));
      const isExcel = (ext === 'xls' || ext === 'xlsx') && (['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''].includes(file.type));
      const isPpt = (ext === 'ppt' || ext === 'pptx') && (['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ''].includes(file.type));
      const isTxt = ext === 'txt' && (file.type === 'text/plain' || !file.type);
      const isZip = ext === 'zip' && (file.type === 'application/zip' || !file.type);
      return allowedTypes.includes(file.type) || isPdf || isDoc || isExcel || isPpt || isTxt || isZip;
    };

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        errors.push(`${file.name}: Arquivo muito grande. Tamanho máximo: 50MB`);
        return;
      }
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: Tipo de arquivo não suportado`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError('');
    }
  };

  // Contador da gravação: efecto apenas [isRecording] — evitar limpar o interval quando o recorder é atribuído.
  useEffect(() => {
    if (!isRecording) return;
    recordingIntervalRef.current = window.setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, [isRecording]);

  // Função para iniciar gravação de áudio
  const handleStartRecording = async () => {
    try {
      discardRecordingRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ];
      const mimeType = mimeCandidates.find((t) => MediaRecorder.isTypeSupported(t));

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        } catch {
          /* noop */
        }
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;

        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          return;
        }

        const outType = recorder.mimeType?.split(';')[0] || 'audio/webm';
        const audioBlob = new Blob(chunks, { type: outType });
        const ext = outType.includes('mp4') ? 'm4a' : 'webm';
        const audioFile = new File([audioBlob], `recording-${Date.now()}.${ext}`, { type: outType });
        setSelectedFiles([audioFile]);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecordingTime(0);
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  // Função para parar gravação de áudio
  const handleStopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    try {
      rec.stop();
    } catch {
      /* noop */
    }
    setIsRecording(false);
  };

  // Função para cancelar arquivo selecionado
  const handleCancelFile = (index?: number) => {
    if (index !== undefined) {
      // Remover arquivo específico
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remover todos os arquivos
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (isRecording) {
        discardRecordingRef.current = true;
        handleStopRecording();
      }
    }
  };

  // Desmontagem: largar microfone e timers
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      discardRecordingRef.current = true;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      recordingStreamRef.current = null;
    };
  }, []);

  // WebSocket para tempo real
  useEffect(() => {
    // Conectar ao WebSocket apenas uma vez
    if (!socketRef.current) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      console.log('🔌 Conectando ao WebSocket...');
      socketRef.current = io(WS_URL, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionAttempts: 5,
        // Token é opcional agora
        ...(token && { auth: { token: token } })
      });

      socketRef.current.on('connect', () => {
        console.log('✅ Connected to WebSocket for WhatsApp Recovery');
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('❌ Erro de conexão WebSocket:', error);
        // Não bloquear conexão por erro de autenticação - WebSocket agora funciona sem token
      });

      socketRef.current.on('new_message', (data) => {
        console.log('📨 [WebSocket] Nova mensagem recebida:', data);
        
        // Padronizar o formato da mensagem
        let message = data.message || data;
        
        // Normalizar attachments se necessário
        if (message.attachments) {
          if (Array.isArray(message.attachments)) {
            message = { ...message, attachments: message.attachments };
          } else if (typeof message.attachments === 'string') {
            try {
              message = { ...message, attachments: JSON.parse(message.attachments) };
            } catch (e) {
              console.error(`Erro ao parsear attachments da mensagem WebSocket ${message.id}:`, e);
              message = { ...message, attachments: [] };
            }
          } else {
            message = { ...message, attachments: [] };
          }
        } else {
          message = { ...message, attachments: [] };
        }

        if (message.conversation_id === selectedConversationRef.current) {
          setMessages(prev => {
            // Verificar se a mensagem já existe (evitar duplicatas do optimistic update)
            const exists = prev.some(msg => msg.id === message.id);
            if (exists) {
              console.log('⚠️ [WebSocket] Mensagem duplicada ignorada:', message.id);
              return prev;
            }
            console.log('✅ [WebSocket] Adicionando nova mensagem ao estado:', message.id);
            const newMessages = [...prev, message];
            // Ordenar mensagens por data de envio (mais antigas primeiro)
            return newMessages.sort((a, b) => 
              new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
            );
          });
          // Scroll para baixo quando nova mensagem chegar
          setTimeout(scrollToBottom, 100);
        }
        
        // Atualizar lista de conversas apenas se for do canal WhatsApp Recovery (whatsapp_recovery)
        // Verificar canal da mensagem ou da conversa
        const messageChannelType = message.channel?.type || data.channel?.type;
        const isWhatsAppRecoveryChannel = messageChannelType === 'whatsapp_recovery';
        
        // Se não tiver informação do canal na mensagem, buscar da conversa na lista
        if (!messageChannelType) {
          const existingConv = conversations.find(c => c.id === message.conversation_id);
          if (existingConv?.channel?.type !== 'whatsapp_recovery') {
            console.log('⚠️ [WebSocket] Mensagem de outro canal, ignorando atualização da lista:', existingConv?.channel?.type);
            return; // Não atualizar se não for do canal WhatsApp Recovery
          }
        } else if (!isWhatsAppRecoveryChannel) {
          console.log('⚠️ [WebSocket] Mensagem de outro canal, ignorando atualização da lista:', messageChannelType);
          return; // Não atualizar se não for do canal WhatsApp Recovery
        }
        
        // Atualizar lista de conversas e reordenar (apenas para canal WhatsApp Recovery)
        setConversations(prev => {
          const conversationExists = prev.some(conv => conv.id === message.conversation_id);
          
          if (conversationExists) {
            // Se a conversa já existe na lista, atualizar
          const updatedConversations = prev.map(conv => {
            if (conv.id === message.conversation_id) {
              // Verificar se a conversa está atualmente selecionada
              const isCurrentlySelected = conv.id === selectedConversationRef.current;
              
              return {
                ...conv,
                  last_message_at: message.sent_at || message.created_at,
                  last_message: message.content ? message.content.substring(0, 100) : conv.last_message,
                  // Só incrementar unread_count se a conversa não estiver selecionada e for mensagem inbound
                unread_count: (message.direction === 'inbound' && !isCurrentlySelected) 
                    ? (conv.unread_count || 0) + 1 
                    : (isCurrentlySelected ? 0 : conv.unread_count || 0)
              };
            }
            return conv;
          });
          
          // Reordenar conversas por last_message_at (mais recentes primeiro)
          return updatedConversations.sort((a, b) => {
            const dateA = new Date(a.last_message_at || a.updated_at || 0);
            const dateB = new Date(b.last_message_at || b.updated_at || 0);
            return dateB.getTime() - dateA.getTime();
          });
          } else {
            // Se a conversa não existe na lista, buscar do backend e verificar canal antes de adicionar
            console.log('📋 [WebSocket] Conversa não encontrada na lista, buscando do backend...');
            axios.get(`${API_BASE_URL}/conversations/${message.conversation_id}`)
              .then(response => {
                if (response.data.success && response.data.data) {
                  const newConversation = response.data.data;
                  
                  // Verificar se é do canal WhatsApp Recovery antes de adicionar
                  if (newConversation.channel?.type === 'whatsapp_recovery') {
                    setConversations(prev => {
                      // Verificar se já foi adicionada (evitar duplicatas)
                      if (prev.some(c => c.id === newConversation.id)) {
                        return prev;
                      }
                      
                      const updated = [newConversation, ...prev];
                      // Reordenar por last_message_at (mais recentes primeiro)
                      return updated.sort((a, b) => {
                        const dateA = new Date(a.last_message_at || a.updated_at || 0);
                        const dateB = new Date(b.last_message_at || b.updated_at || 0);
                        return dateB.getTime() - dateA.getTime();
                      });
                    });
                  } else {
                    console.log('⚠️ [WebSocket] Conversa não é do canal WhatsApp Recovery, não adicionando à lista:', newConversation.channel?.type);
                  }
                }
              })
              .catch(err => {
                console.error('❌ Erro ao buscar conversa do backend:', err);
              });
            
            // Retornar lista atual sem mudanças por enquanto
            return prev;
          }
        });
      });

      socketRef.current.on('conversation_updated', () => {
        // Não recarregar conversas aqui para evitar reordenação ao abrir conversa
        // Apenas atualizar mensagens se necessário
        if (selectedConversation) {
          fetchMessages(selectedConversation);
        }
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('⚠️ WebSocket desconectado:', reason);
        
        // Não tentar reconectar automaticamente se foi por erro de autenticação
        // Apenas reconectar se foi desconexão normal (transport close sem erro de auth)
        if (reason === 'transport close' && reconnectAttemptsRef.current < maxReconnectAttempts) {
          console.log('🔄 Reconexão automática habilitada para desconexão normal');
        } else if (reason === 'io server disconnect') {
          console.log('⚠️ Servidor desconectou o WebSocket. Não reconectando automaticamente.');
        }
      });
    }

    // Entrar na sala da conversa quando selectedConversation mudar
    if (socketRef.current && selectedConversation) {
      socketRef.current.emit('join_conversation', selectedConversation);
    }

    return () => {
      // Não desconectar aqui, apenas sair da sala
      if (socketRef.current && selectedConversation) {
        socketRef.current.emit('leave_conversation', selectedConversation);
      }
    };
  }, [selectedConversation]); // Manter apenas selectedConversation como dependência

  // Carregar conversas iniciais
  useEffect(() => {
    fetchConversations(true);
  }, []);

  // Carregar mensagens quando uma conversa é selecionada
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      // Marcar conversa como lida quando selecionada
      markConversationAsRead(selectedConversation);
    }
  }, [selectedConversation, fetchMessages, markConversationAsRead]);

  // Scroll automático quando mensagens mudarem
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      // Verificar se a data é válida
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 48) {
        return 'Ontem';
      } else {
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      }
    } catch (error) {
      console.error('Error formatting time:', error, dateString);
      return '';
    }
  };

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setEditingName(contact.name || '');
    setIsEditingName(false);
    setOpenLeadDetailsDialog(true);
  };

  const handleCloseLeadDetails = () => {
    setOpenLeadDetailsDialog(false);
    setSelectedContact(null);
    setIsEditingName(false);
    setEditingName('');
  };

  const handleStartEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!selectedContact || !editingName.trim() || editingName === selectedContact.name) {
      setIsEditingName(false);
      return;
    }

    setUpdatingName(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/crm/leads/${selectedContact.id}`, {
        name: editingName.trim()
      });

      if (response.data.success) {
        // Atualizar o contato selecionado
        setSelectedContact({ ...selectedContact, name: editingName.trim() });
        
        // Atualizar na lista de conversas
        setConversations(prev => prev.map(conv => {
          if (conv.contact?.id === selectedContact.id) {
            return {
              ...conv,
              contact: { ...conv.contact, name: editingName.trim() }
            };
          }
          return conv;
        }));
        
        setIsEditingName(false);
      } else {
        throw new Error(response.data.error || 'Erro ao atualizar nome');
      }
    } catch (err: any) {
      console.error('Erro ao atualizar nome do lead:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar nome do lead');
      // Reverter para o nome original em caso de erro
      setEditingName(selectedContact.name || '');
    } finally {
      setUpdatingName(false);
    }
  };

  // Função para identificar região do Brasil pelo prefixo do telefone
  const getRegionFromPhone = (phone?: string): string | null => {
    if (!phone) return null;
    
    // Remover caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Verificar se começa com 55 (código do Brasil)
    if (cleanPhone.startsWith('55')) {
      // Pegar os 2 dígitos após o 55 (DDD)
      const ddd = cleanPhone.substring(2, 4);
      
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    } else if (cleanPhone.length >= 2) {
      // Se não começa com 55, pode ser que o DDD já esteja no início
      const ddd = cleanPhone.substring(0, 2);
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    }
    
    return null;
  };

  // Função para verificar se o lead está no estágio 4 (Venda Realizada)
  const isLeadInStage4 = async (contactId: string): Promise<boolean> => {
    try {
      console.log('🔍 Checking stage for contact:', contactId);
      // Usar a API /crm/all-leads que funciona corretamente
      const response = await axios.get(`${getApiUrl()}/crm/all-leads`);
      if (response.data.success && response.data.data) {
        const allLeads = response.data.data;
        console.log('📊 Total leads found:', allLeads.length);
        
        // Procurar o lead específico
        const targetLead = allLeads.find((lead: any) => lead.contacts?.id === contactId);
        
        if (targetLead) {
          console.log('🎯 Lead found in all-leads:', targetLead.contacts?.name);
          console.log('🎯 Stage order:', targetLead.crm_stages?.stage_order);
          const isStage4 = targetLead.crm_stages?.stage_order === 4;
          console.log('⭐ Is stage 4:', isStage4);
          return isStage4;
        } else {
          console.log('❌ Lead not found in all-leads');
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking lead stage:', error);
      return false;
    }
  };

  // Função para obter o estágio de qualquer lead
  const getLeadStage = (contactId: string): number | null => {
    return allLeadsStages.get(contactId) || null;
  };

  // Função para verificar estágios apenas dos contatos das conversas carregadas (otimizado)
  const checkAllLeadsStages = async (conversations: Conversation[]) => {
    console.log('🔍 checkAllLeadsStages called with conversations:', conversations.length);
    const stage4Set = new Set<string>();
    const stagesMap = new Map<string, number>();
    
    try {
      // Extrair apenas os contact_ids das conversas
      const contactIds = conversations
        .map(conv => conv.contact_id)
        .filter(Boolean) as string[];
      
      if (contactIds.length === 0) {
        console.log('⚠️ No contact IDs to check');
        setStage4Leads(stage4Set);
        setAllLeadsStages(stagesMap);
        return;
      }

      console.log(`📊 Buscando estágios para ${contactIds.length} contatos...`);
      
      // Buscar estágios apenas dos contatos das conversas carregadas (muito mais rápido)
      const response = await axios.post(`${getApiUrl()}/crm/contacts/stages`, {
        contact_ids: contactIds
      });
      
      if (response.data.success && response.data.data) {
        const stagesData = response.data.data; // { contact_id: stage_order }
        
        // Processar os estágios retornados
        Object.entries(stagesData).forEach(([contactId, stageOrder]) => {
          const stage = Number(stageOrder);
          if (stage) {
            stagesMap.set(contactId, stage);
            
            // Se for estágio 4, adicionar ao set
            if (stage === 4) {
              console.log('⭐ Adding to stage 4 set:', contactId);
              stage4Set.add(contactId);
            }
          }
        });
        
        console.log(`✅ Estágios encontrados para ${stagesMap.size} contatos`);
      }
    } catch (error) {
      console.error('Error fetching contact stages:', error);
    }
    
    console.log('🎯 Final stage 4 leads:', Array.from(stage4Set));
    console.log('🎯 All leads stages map:', Object.fromEntries(stagesMap));
    
    setStage4Leads(stage4Set);
    setAllLeadsStages(stagesMap);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showChatList, setShowChatList] = useState(true);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="calc(100vh - 80px)"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">Carregando conversas do WhatsApp Recuperação...</Typography>
      </Box>
    );
  }

  // Função para voltar à lista no mobile
  const handleBackToList = () => {
    setShowChatList(true);
    if (isMobile) {
      setSelectedConversation('');
    }
  };

  return (
    <Box sx={{ 
      p: 0, // Remover padding
      backgroundColor: 'transparent', 
      height: '100vh', // Altura fixa, não minHeight
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
      overflow: 'hidden', // Sem scroll na página principal
    }}>
      <Box sx={{ 
        height: '100%', // 100% da altura do container pai
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}>
      {/* Mobile: Chat em tela cheia quando selecionado */}
      {isMobile && selectedConversation && !showChatList ? (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header do chat mobile */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            bgcolor: '#FF6B35',
            color: '#000000',
            minHeight: '64px',
            paddingTop: 'max(64px, calc(env(safe-area-inset-top) + 64px))',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1
          }}>
            <IconButton 
              onClick={handleBackToList}
              sx={{ 
                mr: 2, 
                color: '#000000',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar 
              src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
              sx={{ 
                mr: 1.5, 
                bgcolor: conversations.find(c => c.id === selectedConversation)?.contact?.name 
                  ? getAvatarColor(conversations.find(c => c.id === selectedConversation)?.contact?.name || '') 
                  : '#25D366',
                width: 32,
                height: 32
              }}
            >
              {conversations.find(c => c.id === selectedConversation)?.contact?.name 
                ? conversations.find(c => c.id === selectedConversation)?.contact?.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                : <WhatsAppIcon />
              }
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WhatsAppIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  WhatsApp
                </Typography>
              </Box>
            </Box>
            
            {/* Botões de ação mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Atribuição de atendente */}
              {selectedConversation && (
                <Box sx={{ 
                  '& .MuiIconButton-root': {
                    color: '#000000',
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(0,0,0,0.08)',
                  }
                }}>
                  <AttendantAssignment conversationId={selectedConversation} />
                </Box>
              )}
              
              <IconButton
                size="small"
                sx={{ color: '#000000' }}
                onClick={() => {
                  // Implementar toggle do bot
                  console.log('Toggle bot');
                }}
              >
                <SmartToyIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#000000' }}
                onClick={() => {
                  // Implementar detalhes do lead
                  console.log('Ver detalhes');
                }}
              >
                <InfoIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#000000' }}
                onClick={() => {
                  // Implementar exclusão
                  console.log('Excluir conversa');
                }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {selectedConversation && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ChatSupportEventsBanner conversationId={selectedConversation} compact />
            </Box>
          )}

          {/* Área de mensagens mobile */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflow: 'auto',
              bgcolor: '#E8F5E8'
            }}>
              {/* Mensagens do chat */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '80%',
                        bgcolor: message.direction === 'outbound' 
                          ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)'
                          : '#FFFFFF',
                        background: message.direction === 'outbound' 
                          ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)'
                          : undefined,
                        color: message.direction === 'outbound' 
                          ? '#0A1E0A'
                          : '#475569',
                        fontWeight: message.direction === 'outbound' ? 500 : 400,
                        p: 2,
                        borderRadius: 3,
                        boxShadow: message.direction === 'outbound' 
                          ? '0 3px 8px rgba(37, 211, 102, 0.35), 0 1px 3px rgba(0, 0, 0, 0.2)'
                          : 1,
                        border: message.direction === 'outbound' ? '2px solid' : undefined,
                        borderColor: message.direction === 'outbound' ? '#8BC88B' : undefined,
                      }}
                    >
                      <Typography sx={{ 
                        mb: 0.5,
                        lineHeight: 1.5,
                        fontSize: '1rem',
                        fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontWeight: message.direction === 'outbound' ? 500 : 400,
                        letterSpacing: '0.01em',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        color: 'inherit',
                      }}>
                        {message.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: message.direction === 'outbound' ? '#1A3A1A' : '#65676B',
                          fontSize: '0.6875rem',
                          fontWeight: 500,
                          letterSpacing: '0.02em',
                          display: 'block'
                        }}
                      >
                        {formatTime(message.sent_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
              </Box>
            </Box>

            {/* Input de mensagem mobile */}
            <Box sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Digite sua mensagem..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(31, 41, 55, 0.3)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 107, 53, 0.2)',
                      borderRadius: '20px',
                      color: '#475569',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(255, 107, 53, 0.4)',
                        boxShadow: '0 4px 12px rgba(255, 107, 53, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(255, 107, 53, 0.6)',
                        boxShadow: '0 4px 16px rgba(255, 107, 53, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input, & textarea': {
                        color: '#475569',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
                <IconButton 
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Desktop: Layout normal */
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0, md: 3 }, 
          height: '100%',
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* Lista de Conversas */}
          <Paper className="glass-blue-card" sx={{ 
            flex: { xs: showChatList ? '1' : '0', md: '0 0 380px' },
            minWidth: { xs: 'auto', md: 380 },
            display: { xs: showChatList ? 'flex' : 'none', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 2 : 1, md: 1 },
            position: { xs: 'relative', md: 'static' },
            overflow: 'hidden',
            borderRadius: '12px', 
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            minHeight: 0, // Importante para flex funcionar corretamente
            '&:hover': {
              borderColor: 'rgba(255, 107, 53, 0.3)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(255, 107, 53, 0.2)',
            },
          }}>
          <Box p={2.5} sx={{ 
            borderBottom: '1px solid rgba(255, 107, 53, 0.1)', 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                p: 0.75,
                borderRadius: '12px',
                bgcolor: 'rgba(255, 107, 53, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WhatsAppIcon sx={{ fontSize: 22, color: '#FF6B35' }} />
              </Box>
              <Typography variant="h6" sx={{ 
                color: '#475569', 
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em'
              }}>
                Conversas
              </Typography>
            </Box>
          </Box>
        
        {error && (
          <Box px={2.5} pt={2}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          </Box>
        )}
        
        {/* Toggle para mostrar/ocultar conversas arquivadas */}
        <Box px={2.5} pb={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#3b82f6',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#3b82f6',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#000000', fontSize: '0.875rem', fontWeight: 500 }}>
                Mostrar arquivadas
              </Typography>
            }
          />
        </Box>
        
        <List sx={{ 
          py: 1, 
          flex: 1, 
          overflow: 'auto',
          minHeight: 0, // Importante para flex funcionar corretamente
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              background: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}>
           {conversations.length === 0 ? (
             <Box sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                 Nenhuma conversa encontrada
               </Typography>
             </Box>
           ) : (
            conversations.map((conversation) => (
              <ListItem
                key={conversation.id}
                component="div"
                onClick={() => {
                  setSelectedConversation(conversation.id);
                  if (isMobile) {
                    setShowChatList(false);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  px: 2,
                  py: 1.25,
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  backgroundColor: selectedConversation === conversation.id 
                    ? 'rgba(255, 107, 53, 0.15)' 
                    : 'transparent',
                  border: selectedConversation === conversation.id 
                    ? '1px solid rgba(255, 107, 53, 0.3)'
                    : '1px solid transparent',
                  borderColor: selectedConversation === conversation.id ? 'rgba(255, 107, 53, 0.8)' : 'transparent',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: selectedConversation === conversation.id 
                      ? 'rgba(255, 107, 53, 0.2)'
                      : 'rgba(255, 107, 53, 0.08)',
                    borderColor: selectedConversation === conversation.id 
                      ? 'rgba(255, 107, 53, 0.8)'
                      : 'rgba(255, 107, 53, 0.2)',
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 56 }}>
                  <Avatar 
                    src={conversation.contact?.avatar_url}
                    sx={{ 
                      bgcolor: conversation.contact?.name ? getAvatarColor(conversation.contact.name) : 'primary.main',
                      width: 48,
                      height: 48,
                      border: `2px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      boxShadow: mode === 'dark' 
                        ? '0 2px 8px rgba(0,0,0,0.3)' 
                        : '0 2px 6px rgba(0,0,0,0.1)',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {conversation.contact?.name 
                      ? conversation.contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon />
                    }
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{ 
                    m: 0,
                    '& .MuiListItemText-primary': {
                      mb: 0.5,
                    },
                    '& .MuiListItemText-secondary': {
                      mt: 0,
                    }
                  }}
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box display="flex" alignItems="center" gap={0.75} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: selectedConversation === conversation.id ? 600 : 500,
                            fontSize: '0.95rem',
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transition: 'font-weight 0.2s',
                          }}
                        >
                          {conversation.contact?.name || 'Usuário'}
                        </Typography>
                        {/* Indicador de estágio do CRM - Apenas estágio 4 */}
                        {conversation.contact?.id && stage4Leads.has(conversation.contact.id) && (
                          <StarIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: '#FFD700',
                              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                              flexShrink: 0,
                            }} 
                          />
                        )}
                        {/* Badge de atendente atribuído */}
                        <AttendantAssignment 
                          conversationId={conversation.id} 
                          compact={true}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Botão de arquivar/desarquivar */}
                        <Tooltip title={conversation.status === 'archived' ? 'Desarquivar conversa' : 'Arquivar conversa'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (conversation.status === 'archived') {
                                handleUnarchiveConversation(conversation.id);
                              } else {
                                handleArchiveConversation(conversation.id);
                              }
                            }}
                            sx={{
                              color: conversation.status === 'archived' ? '#3b82f6' : '#3b82f6',
                              backgroundColor: conversation.status === 'archived' 
                                ? 'rgba(59, 130, 246, 0.1)' 
                                : 'transparent',
                              border: conversation.status === 'archived' 
                                ? '1px solid rgba(59, 130, 246, 0.3)' 
                                : 'none',
                              '&:hover': {
                                backgroundColor: conversation.status === 'archived' 
                                  ? 'rgba(59, 130, 246, 0.2)' 
                                  : 'rgba(59, 130, 246, 0.1)',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            {conversation.status === 'archived' ? (
                              <UnarchiveIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <ArchiveIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {conversation.unread_count > 0 && (
                          <Box
                            sx={{
                              backgroundColor: '#3b82f6',
                              color: '#ffffff',
                            borderRadius: '12px',
                            minWidth: 22,
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            px: 0.75,
                              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                            flexShrink: 0,
                          }}
                        >
                          {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                        </Box>
                        )}
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {(conversation.last_message_at || conversation.updated_at) && (
                        <>
                          <TimeIcon 
                            sx={{ 
                              fontSize: 14, 
                              color: 'text.secondary',
                              opacity: 0.7 
                            }} 
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.8rem',
                              opacity: 0.8,
                              fontWeight: 400,
                            }}
                          >
                            {formatTime(conversation.last_message_at || conversation.updated_at || '')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
          {/* Botão Carregar Mais */}
          {hasMoreConversations && (
            <Box sx={{ px: 2, py: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={loadMoreConversations}
                disabled={loadingMoreConversations}
                startIcon={loadingMoreConversations ? <CircularProgress size={18} /> : null}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.25,
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                  },
                }}
              >
                {loadingMoreConversations ? 'Carregando...' : 'Carregar mais conversas'}
              </Button>
            </Box>
          )}
        </List>
      </Paper>

          {/* Área de Mensagens */}
          <Box sx={{ 
            flex: { xs: showChatList ? '0' : '1', md: 1 },
            minWidth: 0,
            display: { xs: showChatList ? 'none' : 'flex', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 1 : 2, md: 1 },
            position: { xs: 'relative', md: 'static' },
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%', // Altura total do container pai
          }}>
        {selectedConversation ? (
          <>
            {/* Cabeçalho da Conversa - FIXO */}
            <Paper className="glass-blue-card" sx={{ 
              p: 2, 
              borderRadius: '12px 12px 0 0', 
              borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
              flexShrink: 0, // Não encolhe, sempre visível
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
                  sx={{ 
                    bgcolor: (() => {
                      const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                      return contact?.name ? getAvatarColor(contact.name) : 'primary.main';
                    })(),
                    width: 45,
                    height: 45
                  }}
                >
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    return contact?.name 
                      ? contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon />;
                  })()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                      {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
                    </Typography>
                    <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    WhatsApp Recuperação
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Atribuição de atendente */}
                  <AttendantAssignment conversationId={selectedConversation} />
                  
                  {/* Botão de controle do bot */}
                  <BotToggleButton 
                    conversationId={selectedConversation}
                    size="small"
                    onStatusChange={(enabled) => {
                      console.log(`Bot ${enabled ? 'enabled' : 'disabled'} for conversation ${selectedConversation}`);
                    }}
                  />
                  
                  {/* Ícone de estrela para leads do estágio 4 */}
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    console.log('🌟 Rendering star check - contact:', contact?.id, 'stage4Leads:', Array.from(stage4Leads), 'has contact:', stage4Leads.has(contact?.id || ''));
                    return contact && stage4Leads.has(contact.id) ? (
                      <Tooltip title="Lead no estágio 4 - Venda Realizada">
                        <IconButton
                          size="small"
                          sx={{ color: '#FFD700' }}
                        >
                          <StarIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null;
                  })()}
                  
                  <Tooltip title="Ver detalhes do lead">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          handleContactClick(contact);
                        }
                      }}
                    >
                      <PersonIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Atualizar avatar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          updateContactAvatar(contact.id);
                        }
                      }}
                      disabled={updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir lead">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          deleteContact(contact.id);
                        }
                      }}
                      disabled={deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>

            {selectedConversation && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  py: 0.5,
                  borderBottom: '1px solid rgba(59, 130, 246, 0.12)',
                  bgcolor: 'background.paper',
                }}
              >
                <ChatSupportEventsBanner conversationId={selectedConversation} compact />
              </Box>
            )}

            {/* Área de Mensagens */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: '500px',
              background: 'rgba(31, 41, 55, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 107, 53, 0.1)',
              borderRadius: '0 0 12px 12px',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 20px 20px, rgba(255, 107, 53, 0.1) 2px, transparent 2px)
                `,
                backgroundSize: '40px 40px',
                pointerEvents: 'none',
                opacity: 0.5
              }
            }}>
              {/* Container com scroll interno - APENAS ESTE TEM SCROLL */}
              <Box 
                ref={messagesScrollContainerRef}
                sx={{ 
                flex: 1, 
                overflow: 'auto',
                position: 'relative',
                zIndex: 1,
                minHeight: 0, // Importante para flex funcionar
                px: 2, // Padding horizontal
                pb: 2 // Padding bottom
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                {messages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    mb: 1,
                    gap: 1
                  }}
                >
                  {/* Avatar para mensagens recebidas */}
                  {message.direction === 'inbound' && (
                    <Avatar 
                      src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
                      sx={{ 
                        bgcolor: (() => {
                          const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                          return contact?.name ? getAvatarColor(contact.name) : '#25D366';
                        })(),
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem'
                      }}
                    >
                      {(() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        return contact?.name 
                          ? contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                          : <PersonIcon sx={{ fontSize: 16 }} />;
                      })()}
                    </Avatar>
                  )}
                  
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.direction === 'outbound' 
                        ? 'linear-gradient(135deg, #FFB399 0%, #FF8C66 100%)' 
                        : (mode === 'dark' ? 'background.paper' : '#FFFFFF'),
                      background: message.direction === 'outbound' 
                        ? 'linear-gradient(135deg, #FFB399 0%, #FF8C66 100%)' 
                        : undefined,
                      color: message.direction === 'outbound' 
                        ? '#5D1F00' 
                        : (mode === 'dark' ? 'text.primary' : '#475569'),
                      fontWeight: message.direction === 'outbound' ? 500 : 400,
                      borderRadius: message.direction === 'outbound' 
                        ? '18px 18px 4px 18px' 
                        : '18px 18px 18px 4px',
                      boxShadow: message.direction === 'outbound' 
                        ? '0 3px 8px rgba(255, 107, 53, 0.35), 0 1px 3px rgba(0, 0, 0, 0.2)'
                        : (mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.15)'),
                      border: message.direction === 'outbound' ? '2px solid' : 2,
                      borderColor: message.direction === 'outbound' 
                        ? '#FF6B35'
                        : (mode === 'dark' ? 'divider' : '#E0E0E0'),
                    }}
                  >
                    {/* Renderizar anexos */}
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {message.attachments.map((attachment, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            {attachment.type === 'image' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                position: 'relative',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <Box sx={{ position: 'relative' }}>
                                  <img 
                                    src={normalizeAttachmentUrl(attachment.url)} 
                                    alt="Imagem"
                                    style={{ 
                                      width: 'auto', 
                                      maxWidth: '100%',
                                      height: 'auto',
                                      maxHeight: 250,
                                      objectFit: 'contain',
                                      display: 'block',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => setExpandedImage(normalizeAttachmentUrl(attachment.url))}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                  <Tooltip title="Clique para ampliar">
                                    <Box 
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: '#fff',
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.5,
                                        fontSize: '0.75rem',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      <ZoomInIcon sx={{ fontSize: 16 }} />
                                      Ampliar
                                    </Box>
                                  </Tooltip>
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    zIndex: 10
                                  }}>
                                    <IconButton 
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `image_${message.id}.jpg`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ 
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }
                                      }}
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                {(attachment.width || attachment.height) && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1, color: 'text.secondary' }}>
                                    {attachment.width && attachment.height && `${attachment.width} × ${attachment.height}`}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {attachment.type === 'video' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <Box sx={{ position: 'relative' }}>
                                  <video 
                                    controls
                                    preload="metadata"
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      maxHeight: 400,
                                      display: 'block'
                                    }}
                                  >
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/mp4" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/webm" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/ogg" />
                                    Seu navegador não suporta vídeos.
                                  </video>
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    zIndex: 10
                                  }}>
                                    <IconButton 
                                      size="small"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `video_${message.id}.mp4`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ 
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }
                                      }}
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                {attachment.duration && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1, color: 'text.secondary' }}>
                                    Duração: {Math.floor(attachment.duration / 60)}:{(attachment.duration % 60).toString().padStart(2, '0')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {attachment.type === 'audio' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mp3" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/wav" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  {attachment.title && (
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      {attachment.title}
                                      {attachment.performer && ` - ${attachment.performer}`}
                                    </Typography>
                                  )}
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = attachment.title 
                                        ? `${attachment.title}.mp3` 
                                        : `audio_${message.id}.mp3`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'voice' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/webm" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ flex: 1 }}>
                                    🎤 Mensagem de voz
                                  </Typography>
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = `voice_${message.id}.ogg`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'document' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Typography variant="body2">📎</Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {attachment.file_name || 'Documento'}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(normalizeAttachmentUrl(attachment.url), '_blank')}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Box>
                            )}
                            {attachment.type === 'sticker' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                display: 'inline-block'
                              }}>
                                <img 
                                  src={normalizeAttachmentUrl(attachment.url)} 
                                  alt="Sticker"
                                  style={{ 
                                    width: 120, 
                                    height: 120,
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Renderizar texto da mensagem */}
                    {message.content && (
                      <Typography sx={{
                        fontSize: '1rem',
                        lineHeight: 1.5,
                        fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontWeight: message.direction === 'outbound' ? 500 : 400,
                        letterSpacing: '0.01em',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        color: 'inherit',
                      }}>
                        {message.content}
                      </Typography>
                    )}
                    
                    <Typography variant="caption" sx={{ 
                      opacity: 0.7,
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      letterSpacing: '0.02em',
                      color: message.direction === 'outbound' ? '#1A3A1A' : undefined,
                    }}>
                      {formatTime(message.sent_at)}
                    </Typography>
                                      </Paper>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
                </Box>
              </Box>
            </Box>

            {/* Campo de Envio - FIXO */}
            <Paper sx={{ 
              p: 2, 
              borderRadius: '0 0 12px 12px', 
              background: 'rgba(31, 41, 55, 0.4)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 107, 53, 0.2)',
              borderTop: '1px solid rgba(255, 107, 53, 0.1)',
              flexShrink: 0, // Não encolhe, sempre visível
              boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.2), 0 -2px 6px rgba(255, 107, 53, 0.1)',
            }}>
              {/* Indicador de arquivos selecionados */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {selectedFiles.length === 1 ? (
                    // Mostrar prévia detalhada para 1 arquivo
                    <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {selectedFiles[0].type.startsWith('image/') && (
                        <Box
                          component="img"
                          src={URL.createObjectURL(selectedFiles[0])}
                          alt={selectedFiles[0].name}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1,
                            flexShrink: 0
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                        {(selectedFiles[0].type.startsWith('audio/') || selectedFiles[0].name.includes('recording')) && '🎤 '}
                        {selectedFiles[0].name}
                        {selectedFiles[0].size > 0 && ` (${(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB)`}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCancelFile(0)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    // Mostrar apenas o número de arquivos quando houver múltiplos
                    <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        📎 {selectedFiles.length} arquivo{selectedFiles.length > 1 ? 's' : ''} anexado{selectedFiles.length > 1 ? 's' : ''}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCancelFile()}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              {/* Indicador de gravação */}
              {isRecording && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'error.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                  <Typography variant="body2" sx={{ flex: 1, color: 'error.main', fontWeight: 500 }}>
                    Gravando... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: 'error.main' }}>
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="flex-end">
                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {/* Botão de anexar arquivo */}
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  <AttachFileIcon />
                </IconButton>

                {/* Botão de gravar áudio */}
                <IconButton
                  color={isRecording ? 'error' : 'primary'}
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={sendingMessage || selectedFiles.length > 0}
                  sx={{ flexShrink: 0 }}
                >
                  {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>

                {/* Botão de mensagens rápidas */}
                <QuickMessagesButton
                  onSelectMessage={(message) => {
                    setNewMessage(message);
                    messageInputRef.current?.focus();
                  }}
                  channel="whatsapp_recovery"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={selectedFiles.length > 0 ? "Adicione uma legenda (opcional)..." : "Digite sua mensagem..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  inputRef={messageInputRef}
                  disabled={sendingMessage || isRecording}
                  multiline
                  maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.12)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input, & textarea': {
                        color: '#475569',
                        '&::placeholder': {
                          color: '#94a3b8',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Box>
            </Paper>
          </>
        ) : (
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              background: 'rgba(31, 41, 55, 0.4)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 107, 53, 0.2)',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(255, 107, 53, 0.1)',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ color: '#475569' }}>
              Selecione uma conversa para começar
            </Typography>
          </Paper>
        )}
          </Box>
        </Box>
      )}
      </Box>

      {/* Dialog para Imagem Ampliada */}
      <Dialog 
        open={!!expandedImage} 
        onClose={() => setExpandedImage(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 2,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Imagem Ampliada
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => {
                if (expandedImage) {
                  const link = document.createElement('a');
                  link.href = normalizeAttachmentUrl(expandedImage);
                  link.download = `image_${Date.now()}.jpg`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={() => setExpandedImage(null)}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          position: 'relative',
          minHeight: '60vh'
        }}>
          {expandedImage && (
            <img 
              src={normalizeAttachmentUrl(expandedImage || '')} 
              alt="Imagem ampliada"
              style={{ 
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                cursor: 'zoom-out'
              }}
              onClick={() => setExpandedImage(null)}
              onError={(e) => {
                console.error('Erro ao carregar imagem:', e);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para Detalhes do Lead */}
      <Dialog open={openLeadDetailsDialog} onClose={handleCloseLeadDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={selectedContact?.avatar_url || undefined}
              sx={{
                bgcolor: selectedContact?.avatar_url ? 'transparent' : '#25D366' + '15',
                color: '#25D366',
                fontWeight: 600,
                width: 48,
                height: 48,
                fontSize: '1.2rem'
              }}
            >
              {!selectedContact?.avatar_url && (selectedContact?.name || 'Lead').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedContact?.name || 'Lead'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366' }} />
                <Typography variant="body2" color="text.secondary">
                  WhatsApp Recovery Lead
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Informações Básicas */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Informações Básicas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Nome
                  </Typography>
                  {isEditingName ? (
                    <TextField
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={updatingName}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName();
                        } else if (e.key === 'Escape') {
                          handleCancelEditName();
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                        {selectedContact?.name || 'Não informado'}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleStartEditName}
                        title="Editar nome"
                        sx={{ ml: 'auto' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.email || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.phone || 'Não informado'}
                  </Typography>
                  {selectedContact?.phone && getRegionFromPhone(selectedContact.phone) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                      📍 {getRegionFromPhone(selectedContact.phone)}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Canal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      WhatsApp Recovery
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* IDs do WhatsApp */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                IDs do WhatsApp
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    WhatsApp ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.whatsapp_id || 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID Interno
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.id || 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Campos Customizados */}
            {selectedContact?.custom_fields && Object.keys(selectedContact.custom_fields).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Campos Customizados
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {selectedContact && selectedContact.custom_fields && Object.entries(selectedContact.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {String(value) || 'Não disponível'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeadDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppRecovery; 